// @vendors
const Immutable = require('immutable');
const { get, findIndex } = require('lodash');

// @utils
const {
    mapDataToAnswers,
    getLastAnswerGeneral,
    isPredatedAnswers,
} = require('../utilities/RASuitabilityTestHelper');
const { formatText } = require('core/i18n').i18n;

// @constants
const actionTypes = require('../raConstants/actionTypes');
const { YEARS } = require('../raConstants/index');

const accordionIcons = {
    answer1: 'icon-depositos-bancarios',
    answer2: 'icon-renta-fija',
    answer3: 'icon-renta-variable',
    answer4: 'icon-fondos-planes-seguros',
    answer5: 'icon-productos-con-garantia',
    answer6: 'icon-productos-sin-garantia',
    answer7: 'icon-bonos-convertibles',
    answer8: 'icon-participa-preferentes',
    answer9: 'icon-productos-inmobiliarios',
    answer10: 'icon-prod-capital-riesgo',
    answer11: 'icon-derivados-mercado',
    answer12: 'icon-derivados-otc',
};

// @Contact Center
const {
    RA_ZERO_QUESTION_ID,
} = require('../raConstants/contactCenter/zeroQuestionCodes');
const actionTypesContactCenter = require('../raConstants/contactCenter/actionTypes');
const {
    zeroQuestionPresent,
    zeroQuestionWasAnswered,
} = require('../utilities/contactCenter/RAZeroQuestionHelper');

const initialState = Immutable.fromJS({
    answers: Immutable.OrderedMap({
        '101': '',
        '102': '',
        '103': '',
        '104': '',
        '105': 'answer1',
        '106': 'answer1',
        '107': 'answer1',
        '108': 'answer1',
        '109': 'answer1',
        '110': 'answer1',
        '111': 'answer1',
        '112': 'answer1',
        '113': 'answer1',
        '114': 'answer1',
        '115': 'answer1',
        '217': '',
        '218': 5,
        '219': 15,
        '320': '',
        '321': '',
        '322': 1,
        '323': '',
        '701': 10000,
        '400': '',
        '401': '',
        '402': '',
        '403': '',
        '404': '',
        '405': '',
        '500': '',
    }),
    validations: {},
    coherenceError: {
        error: false,
        message: '',
        allowToPass: false,
        isNonBlocking: false,
    },
    accordionStep: {
        expandedRowId: [],
    },
    answeredQuestionIds: [],
    error: null,
    isFetchingQuestions: false,
    errorFetchingQuestions: false,
    isFetchingResults: false,
    isSendingTest: false,
    questions: {},
    copyVersion: null,
    lastAnswer: {},
    readOnly: false,
    success: false,
    successDone: false,
    testCompleted: false,
    results: null,
    isFetchingHistoricalTests: false,
    fetchedHistoricalTest: false,
    retryStatus: 0,
    retryMainText: '',
    retrySecondaryText: '',
    retryTitle: '',
    dropValue: YEARS,
    isNonresidentialModalOpen: false,
    isErrorFetchingCountryOfResidenceModalOpen: false,
    type: '',
    fromSetGoal: false,
    dueDate: '',
    retryTest: false,
    retryReason: '', // @verify possible values, for now answer[1, 2, 3, 4]
    buttonRenewClicked: false,
    isTestFrom: '',
    isReviewing: false,
    portfolioId: null,
    isZeroQuestionAnswered: false,
    continueWithUnsuccess: false,
    generatedPdf: null,
    fetchingPdf: false,
    errorOnPdf: false,
    currentSustainabilityQuestionId: '',
    showModalForNewQuestions: false,
    retrieveTestVersion: '',
    investmentExperience: null,
    isOpenModalInvestmentExperience: false,
    operationProcessIdFetching: null,
    operationProcessIdError: false,
    operationProcessId: null,
});

function updateExpandedRow(payload, state) {
    const previousState = state.getIn(['accordionStep', 'expandedRowId']);
    if (previousState.includes(payload.expandedRowId)) {
        return state.setIn(
            ['accordionStep', 'expandedRowId'],
            previousState.filter(rowId => rowId !== payload.expandedRowId),
        );
    } else {
        return state.setIn(
            ['accordionStep', 'expandedRowId'],
            [...previousState, payload.expandedRowId],
        );
    }
}

function mapQuestionsToValidations(questions) {
    let validations = {};
    const filteredQuestions = questions.filter(question => {
        return question.answerOptions.some(answer => answer.unsuitable);
    }, []);

    filteredQuestions.forEach(question => {
        let answers = {};
        question.answerOptions
            .filter(answer => answer.unsuitable)
            .forEach(answer => {
                let previousQuestions = {};
                const unsuitable = answer.unsuitable || [];
                // @TODO: Exchange idAnswer to id idQuestion and viceversa
                unsuitable.forEach(previousQuestion => {
                    if (
                        previousQuestions.hasOwnProperty(  // eslint-disable-line
                            previousQuestion.idQuestion,
                        )
                    ) {
                        previousQuestions[previousQuestion.idQuestion][
                            previousQuestion.idAnswer
                        ] = previousQuestion.errorCodeDescription;
                    } else {
                        previousQuestions[previousQuestion.idQuestion] = {
                            [previousQuestion.idAnswer]:
                                previousQuestion.errorCodeDescription,
                        };
                    }
                });
                answers[answer.id] = previousQuestions;
            });

        validations[question.id] = answers;
    });

    return validations;
}

const mapRadioButtons = answer => ({
    id: answer.id,
    label: {
        title: answer.title,
        description: answer.description,
    },
});

const findQuestionIndex = (questions, id) => {
    return findIndex(questions, question => question.id === id);
};

const mapQuestions = (questions, state) => {
    const mappedQuestions = {
        '101': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '101')}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(questions, '101')}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
        },
        '102': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '102')}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(questions, '102')}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
        },
        '103': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '103')}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(questions, '103')}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
        },
        '104': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '104')}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(questions, '104')}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
        },
        '217': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '217')}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(questions, '217')}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
        },
        '218': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '218')}].title`,
                '',
            ),
        },
        '219': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '219')}].title`,
                '',
            ),
            subtitle: get(
                questions,
                `[${findQuestionIndex(questions, '219')}].description`,
                '',
            ),
        },
        '320': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '320')}].title`,
                '',
            ),
        },
        '322': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '322')}].title`,
                '',
            ),
            answers: [
                {
                    id: 1,
                    label: get(
                        questions,
                        `[${findQuestionIndex(
                            questions,
                            '322',
                        )}].answerOptions[3].title`,
                        '',
                    ),
                },
                {
                    id: 2,
                    label: get(
                        questions,
                        `[${findQuestionIndex(
                            questions,
                            '322',
                        )}].answerOptions[2].title`,
                        '',
                    ),
                },
                {
                    id: 3,
                    label: get(
                        questions,
                        `[${findQuestionIndex(
                            questions,
                            '322',
                        )}].answerOptions[1].title`,
                        '',
                    ),
                },
                {
                    id: 4,
                    label: get(
                        questions,
                        `[${findQuestionIndex(
                            questions,
                            '322',
                        )}].answerOptions[0].title`,
                        '',
                    ),
                },
            ],
        },
        '323': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '323')}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(questions, '323')}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
        },
        [state.get('currentSustainabilityQuestionId')]: {
            title: get(
                questions,
                `[${findQuestionIndex(
                    questions,
                    state.get('currentSustainabilityQuestionId'),
                )}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(
                    questions,
                    state.get('currentSustainabilityQuestionId'),
                )}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
        },
        '402': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '402')}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(questions, '402')}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
        },
        '403': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '403')}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(questions, '403')}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
        },
        '404': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '404')}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(questions, '404')}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
        },
        '405': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '405')}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(questions, '405')}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
        },
        '500': {
            title: get(
                questions,
                `[${findQuestionIndex(questions, '500')}].title`,
                '',
            ),
            answers: get(
                questions,
                `[${findQuestionIndex(questions, '500')}].answerOptions`,
                [],
            ).map(answer => mapRadioButtons(answer)),
            description: get(
                questions,
                `[${findQuestionIndex(questions, '500')}].description`,
                '',
            ),
        },
    };

    if (__CONTACT_CENTER__) {
        const zeroQuestionIndex = findQuestionIndex(
            questions,
            RA_ZERO_QUESTION_ID,
        );

        if (zeroQuestionIndex >= 0) {
            mappedQuestions[RA_ZERO_QUESTION_ID] = {
                title: get(questions, `[${zeroQuestionIndex}].title`, ''),
                answers: get(
                    questions,
                    `[${zeroQuestionIndex}].answerOptions`,
                    [],
                ).map(answer => mapRadioButtons(answer)),
            };
        }
    }

    return mappedQuestions;
};

const mapAccordionButtons = button => ({
    id: button.id,
    label: button.title,
});

const accordionQuestionId = '105';
const accordionAnswers = 106;

const mapAccordionRows = (row, index, questions) => ({
    id: `${index + accordionAnswers}`,
    title: formatText(`raSuitabilityTest-accordionQuestion_title_${row.id}`),
    icon: accordionIcons[row.id],
    text: formatText(
        `raSuitabilityTest-accordionQuestion_description_${row.id}`,
    ),
    buttons: questions[
        findQuestionIndex(questions, `${index + accordionAnswers}`)
    ].answerOptions.map(button => mapAccordionButtons(button)),
});

const getAnswerAccordingToTime = timesHired => {
    if (timesHired === 0) {
        return 'answer1';
    } else if (timesHired === 1) {
        return 'answer2';
    } else if (timesHired >= 2) {
        return 'answer3';
    }
};

const mapAccordionStep = questions => {
    return {
        title: get(
            questions,
            `[${findQuestionIndex(questions, accordionQuestionId)}].title`,
            '',
        ),
        subtitle: get(
            questions,
            `[${findQuestionIndex(questions, accordionQuestionId)}].subtitle`,
            '',
        ),
        expandedRowId: [],
        rows: get(
            questions,
            `[${findQuestionIndex(
                questions,
                accordionQuestionId,
            )}].answerOptions`,
            [],
        ).map((row, index) => mapAccordionRows(row, index, questions)),
    };
};

const RASuitabilityTest = (state = initialState, action) => {
    switch (action.type) {
        case actionTypesContactCenter.RA_SUITABILITY_TEST_UPDATE_ZERO_QUESTION:
            return state.setIn(
                ['answers', RA_ZERO_QUESTION_ID],
                action.payload.value,
            );
        case actionTypes.RA_SUITABILITY_TEST_ANSWERED_QUESTION_ZERO:
            return state.set('isZeroQuestionAnswered', true);
        case actionTypes.RA_SUITABILITY_TEST_BUTTON_RENEW_PRESSED:
            return state.set('buttonRenewClicked', true);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_KNOWLEDGE:
            return state.setIn(['answers', '101'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_FINANCIAL:
            return state.setIn(['answers', '102'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_TOOLS:
            return state.setIn(['answers', '103'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_MARKET_RISK:
            return state.setIn(['answers', '104'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_CAPITAL:
            return state.setIn(['answers', '322'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_BACKUP:
            return state.setIn(['answers', '323'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_TEMPORAL_HORIZON:
            return state.setIn(['answers', '218'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_VOLATILITY_VALUE:
            return state.setIn(['answers', '219'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_VOLATILITY_INPUT_VALUE:
            return state.setIn(['answers', '701'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_INCOMING_VALUE:
            return state.setIn(['answers', '320'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_OUTGOING_VALUE:
            return state.setIn(['answers', '321'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_INVESTMENT_HORIZON_SLIDER:
            return state.setIn(['answers', '218'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_EXPAND_ACCORDION_ROW:
            return updateExpandedRow(action.payload, state);
        case actionTypes.RA_SUITABILITY_TEST_RETRY_REASON_CHANGED:
            return state.merge({
                retryReason: action.payload.value,
            });
        case actionTypes.RA_SUITABILITY_TEST_SELECT_ACCORDION_BUTTON:
            return state
                .setIn(
                    ['answers', action.payload.rowId],
                    action.payload.buttonId,
                )
                .setIn(['coherenceError', 'allowToPass'], false);
        case actionTypes.RA_SUITABILITY_TEST_SEND_TEST_IN_PROGRESS:
            return state.merge({
                isSendingTest: true,
            });
        case actionTypes.RA_SUITABILITY_TEST_SEND_TEST_SUCCESS:
            return state.merge({
                isSendingTest: false,
                isReviewing: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_UPDATE_OBJECTIVE:
            return state.setIn(['answers', '217'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_SET_ORIGIN:
            return state.merge({
                isTestFrom: action.payload.value,
                portfolioId: action.payload.id,
            });
        case actionTypes.RA_SUITABILITY_TEST_SET_READ_ONLY:
            return state.set('readOnly', action.payload);
        case actionTypes.RA_SUITABILITY_TEST_SET_COHERENCE_ERROR:
            return state.mergeIn(['coherenceError'], {
                error: true,
                message: action.payload.message,
                allowToPass: action.payload.allowToPass,
                isNonBlocking: action.payload.isNonBlocking,
            });
        case actionTypes.RA_SUITABILITY_TEST_RESET_COHERENCE_ERROR:
            return state.mergeIn(['coherenceError'], {
                error: false,
                message: '',
            });
        case actionTypes.RA_SUITABILITY_TEST_HISTORICAL_TEST_REQUEST:
            return state.set('isFetchingHistoricalTests', true);
        case actionTypes.RA_SUITABILITY_TEST_HISTORICAL_TEST_SUCCESS:
            const { results, retryTestStatus } = action.payload;
            const title = get(retryTestStatus, 'message.title');
            const mainText = get(retryTestStatus, 'message.mainText');
            const secondaryText = get(retryTestStatus, 'message.secondaryText');
            return state.merge({
                results: results,
                isFetchingHistoricalTests: false,
                fetchedHistoricalTest: true,
                // TODO: https://jira.ods.ok-cloud.net/browse/FRONTCC-78
                // Until the microservices are merged it is needed to exclude the retryTestStatus fields
                ...(!__CONTACT_CENTER__
                    ? {
                          retryStatus: retryTestStatus.status,
                          retryMainText: mainText,
                          retrySecondaryText: secondaryText,
                          retryTitle: title,
                      }
                    : {}),
            });
        case actionTypes.RA_SUITABILITY_GET_TEST_QUESTIONS_REQUEST:
            return state.merge({
                isFetchingQuestions: true,
                errorFetchingQuestions: false,
            });
        case actionTypes.RA_SUITABILITY_HANDLE_CLOSE_MODAL_INVESTMENT_EXPERIENCE:
            return state.merge({
                isOpenModalInvestmentExperience: action.payload,
            });
        case actionTypes.RA_SUITABILITY_GET_INVESTMENT_EXPERIENCE_SUCCESS:
            const {
                dataInvestmentExperience,
                showInvestmentExperienceModal,
            } = action.payload;
            if (showInvestmentExperienceModal) {
                const listAnswers = [
                    { id: '106', option: 'A' },
                    { id: '107', option: 'B' },
                    { id: '110', option: 'E' },
                    { id: '111', option: 'F' },
                    { id: '112', option: 'G' },
                    { id: '114', option: 'I' },
                ];
                const copyState = state.toJS();
                dataInvestmentExperience.forEach(experience => {
                    const timeHired = experience.timesHired;
                    const answer = listAnswers.find(
                        element =>
                            element.option === experience.productFamilyId,
                    );
                    if (answer) {
                        copyState.answers[answer.id] = getAnswerAccordingToTime(
                            timeHired,
                        );
                    }
                });
                return state.merge({
                    answers: copyState.answers,
                    investmentExperience: dataInvestmentExperience,
                    isOpenModalInvestmentExperience: showInvestmentExperienceModal,
                });
            } else {
                return state.merge({
                    investmentExperience: dataInvestmentExperience,
                });
            }
        case actionTypes.RA_SUITABILITY_GET_TEST_QUESTIONS_SUCCESS:
            const { data, readOnly } = action.payload;
            const questions = get(data, 'getTestQuestionsOutput.questions', []);
            const copyVersion = get(data, 'getTestQuestionsOutput.copyVersion');
            const answers =
                isPredatedAnswers() && !!get(data, 'mappedTest.nextAnswer', [])
                    ? get(data, 'mappedTest.nextAnswer', [])
                    : [];
            const dueDate = get(data, 'mappedTest.date');
            const answeredQuestionIds = answers.map(
                answer => answer.idQuestion,
            );
            const retrieveTestVersion = get(data, 'mappedTest.version');
            const immQuestions = Immutable.fromJS(
                mapQuestions(questions, state),
            );
            const isZeroQuestionPresent = zeroQuestionPresent(
                immQuestions,
                readOnly,
            );
            const lastAnswer = Immutable.fromJS(
                getLastAnswerGeneral(
                    isZeroQuestionPresent,
                    answeredQuestionIds,
                ),
            );

            return state
                .set(
                    'isZeroQuestionAnswered',
                    zeroQuestionWasAnswered(immQuestions),
                )
                .set('isFetchingQuestions', false)
                .set('errorFetchingQuestions', false)
                .set('questions', immQuestions)
                .set('copyVersion', copyVersion)
                .set(
                    'accordionStep',
                    Immutable.fromJS(mapAccordionStep(questions)),
                )
                .set(
                    'validations',
                    Immutable.fromJS(mapQuestionsToValidations(questions)),
                )
                .set('lastAnswer', lastAnswer)
                .set('dueDate', dueDate)
                .set('answeredQuestionIds', answeredQuestionIds)
                .set('retrieveTestVersion', retrieveTestVersion)
                .mergeDeep({
                    answers: answers.length
                        ? Immutable.OrderedMap(mapDataToAnswers(answers))
                        : state.get('answers'),
                });
        case actionTypes.RA_SUITABILITY_GET_TEST_QUESTIONS_FAILURE:
            return state.merge({
                isFetchingQuestions: false,
                errorFetchingQuestions: true,
            });
        case actionTypes.RA_SUITABILITY_GET_TEST_RESULT_REQUEST:
            return state.merge({
                isFetchingResults: true,
            });
        case actionTypes.RA_SUITABILITY_GET_TEST_RESULT_SUCCESS:
            return state.merge({
                results: action.payload.results,
                isFetchingResults: false,
                success: get(
                    action.payload.results && action.payload.results[0],
                    'passedTest',
                    false,
                ),
            });
        case actionTypes.RA_SUITABILITY_GET_TEST_RESULT_FAILURE:
            return state.merge({
                isFetchingResults: false,
                error: action.payload.error,
                success: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_COMPLETED:
            return state.merge({
                testCompleted: true,
            });
        case actionTypesContactCenter.RA_SUITABILITY_TEST_SIGN_TEST_SUCCESS:
            return state.merge({
                successDone: true,
            });
        case actionTypes.RA_CONTINUE_WITH_UNSUCCESS_TEST:
            return state.set('continueWithUnsuccess', true);
        case actionTypes.RA_SUITABILITY_TEST_RESET:
            return initialState;
        case actionTypes.RA_SUITABILITY_TEST_REVIEW_IN_PROGRESS:
            return state.merge({
                isReviewing: true,
            });
        case actionTypes.RA_NONRESIDENTIAL_MODAL_VALIDATION:
            const errorFetchingCountryOfResidence =
                __CONTACT_CENTER__ && action.payload.profile.get('error');
            if (errorFetchingCountryOfResidence) {
                return state.merge({
                    isErrorFetchingCountryOfResidenceModalOpen: true,
                });
            }

            const resident = action.payload.profile.get('paisResidencia');
            const originCountry = action.payload.profile.get('originCountry');
            return state.merge({
                isNonresidentialModalOpen:
                    resident != originCountry ? true : false,
                type: action.payload.type,
            });
        case actionTypes.RA_NONRESIDENTIAL_MODAL_CLOSE:
            return state.merge({
                isNonresidentialModalOpen: false,
            });
        case actionTypesContactCenter.RA_ERROR_FETCHING_COUNTRY_OF_RESIDENCE_MODAL_CLOSE:
            return state.merge({
                isErrorFetchingCountryOfResidenceModalOpen: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_RESET_ANSWERS:
            return state.merge({
                answers: initialState.get('answers'),
                lastAnswer: false,
                answeredQuestionIds: [],
            });
        case actionTypes.RA_SUITABILITY_TEST_RESET_TEST_COMPLETED:
            return state.merge({
                testCompleted: false,
            });
        case actionTypes.RA_SET_FROM_SET_GOAL:
            return state.merge({
                fromSetGoal: true,
            });
        case actionTypes.RA_SUITABILITY_TEST_RETRY_TEST_ENABLED:
            return state.merge({
                retryTest: true,
            });
        case actionTypes.RA_SUITABILITY_TEST_GET_PDF_TEST_FETCHING:
            return state.merge({
                fetchingPdf: true,
                errorOnPdf: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_GET_PDF_TEST_SUCCESS:
            return state.merge({
                generatedPdf: action.payload.generatedPdf,
                fetchingPdf: false,
                errorOnPdf: false,
            });
        case actionTypes.RA_SUITABILITY_TEST_GET_PDF_TEST_FAILURE:
            return state.merge({
                generatedPdf: null,
                fetchingPdf: false,
                errorOnPdf: true,
            });
        case actionTypes.RA_SUITABILITY_SET_SUSTAINABILITY_QUESTION_ID:
            return state.merge({
                currentSustainabilityQuestionId: action.payload,
            });
        case actionTypes.RA_SUITABILITY_TEST_SET_SUSTAINABILITY_ANSWER:
            return state.setIn(
                ['answers', state.get('currentSustainabilityQuestionId')],
                action.payload.value,
            );
        case actionTypes.RA_SUITABILITY_TEST_SET_SUSTAINABILITY_INVESTMENT_FACTORS:
            return state.setIn(['answers', '402'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_SET_SUSTAINABILITY_PIAS:
            return state.setIn(['answers', '403'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_SET_SUSTAINABILITY_TAXONOMIA:
            return state.setIn(['answers', '404'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_SET_SUSTAINABILITY_INVESTMENT_OBJECTIVE:
            return state.setIn(['answers', '405'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_SET_SUSTAINABILITY_OUR_STRATEGIES:
            return state.setIn(['answers', '500'], action.payload.value);
        case actionTypes.RA_SUITABILITY_TEST_SHOW_MODAL_NEW_QUESTIONS:
            return state.merge({
                showModalForNewQuestions: action.payload,
            });
        case actionTypes.RA_SUITABILITY_TEST_DMS_INIT_FETCHING:
            return state.merge({
                operationProcessIdFetching: true,
                operationProcessIdError: false,
                operationProcessId: null,
            });
        case actionTypes.RA_SUITABILITY_TEST_DMS_INIT_SUCCESS:
            return state.merge({
                operationProcessIdFetching: false,
                operationProcessIdError: false,
                operationProcessId: action.payload.operationProcessId,
            });
        case actionTypes.RA_SUITABILITY_TEST_DMS_INIT_ERROR:
            return state.merge({
                operationProcessIdFetching: false,
                operationProcessIdError: true,
                operationProcessId: null,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_SAVE_TERMS_AND_CONDITIONS:
            return state.merge({
                termsAndConditions: action.payload,
            });
        default:
            return state;
    }
};

module.exports = RASuitabilityTest;
